import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ListTree, Plus, Edit2, Trash2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { financialService } from '@services/financialService';
import type { Company } from '@/types/company';
import type { AccountingPlanNode, DreGroup } from '@/types/financial';
import { movementLabel, movementBadgeColor } from './financialUtils';
import {
  LoadingSpinner,
  ErrorBox,
  SuccessBox,
  PermissionFallback,
  ModalShell,
  selectCls,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './financialShared';

type FilterChip = 'all' | 'unmapped';

// ─── Modal CRUD de categoria ────────────────────────────────────

interface PlanModalState {
  node: AccountingPlanNode | null; // null = nova
  parent: AccountingPlanNode | null; // pai (nova filha) ou null (raiz)
}

const PlanModal: React.FC<{
  state: PlanModalState;
  onSave: (data: { description: string; movement: number; disabled: boolean }) => Promise<void>;
  onClose: () => void;
}> = ({ state, onSave, onClose }) => {
  const isEdit = state.node != null;
  const [description, setDescription] = useState(state.node?.description ?? '');
  const [movement, setMovement] = useState<number>(state.node?.movement ?? (state.parent ? state.parent.movement : 2));
  const [disabled, setDisabled] = useState(state.node?.disabled ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = isEdit
    ? 'Editar Categoria'
    : state.parent
      ? `Nova Categoria em "${state.parent.description}"`
      : 'Nova Categoria Raiz';

  const handleSubmit = async () => {
    setError(null);
    if (!description.trim()) {
      setError('Informe a descrição');
      return;
    }
    try {
      setSaving(true);
      await onSave({ description: description.trim(), movement, disabled });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar categoria');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={title}
      icon={<ListTree size={20} className="text-emerald-600" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} className={primaryBtnCls}>
            {saving ? <RefreshCw size={14} className="animate-spin" /> : null}
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div>
        <label className={labelCls}>Descrição *</label>
        <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={100} autoFocus />
      </div>
      <div>
        <label className={labelCls}>Movimento *</label>
        <select className={selectCls} value={movement} onChange={(e) => setMovement(Number(e.target.value))}>
          <option value={0}>Saída</option>
          <option value={1}>Entrada</option>
          <option value={2}>Outros</option>
        </select>
      </div>
      {isEdit && (
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            checked={disabled}
            onChange={(e) => setDisabled(e.target.checked)}
          />
          Categoria desabilitada (não aparece em novos lançamentos)
        </label>
      )}
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── Linha da árvore ────────────────────────────────────────────

interface PlanRowProps {
  node: AccountingPlanNode;
  depth: number;
  expanded: Set<number>;
  canWrite: boolean;
  dreGroups: DreGroup[];
  dreSavingId: number | null;
  onToggle: (erpId: number) => void;
  onDreChange: (node: AccountingPlanNode, dreGroupId: number | null) => void;
  onAddChild: (node: AccountingPlanNode) => void;
  onEdit: (node: AccountingPlanNode) => void;
  onDelete: (node: AccountingPlanNode) => void;
}

const PlanRow: React.FC<PlanRowProps> = ({
  node,
  depth,
  expanded,
  canWrite,
  dreGroups,
  dreSavingId,
  onToggle,
  onDreChange,
  onAddChild,
  onEdit,
  onDelete,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.has(node.erpId);
  const isParent = depth === 0 || hasChildren;
  const dreSaving = dreSavingId === node.accountingPlanId;

  return (
    <>
      <tr className={clsx('border-b border-slate-100 hover:bg-slate-50', node.disabled && 'opacity-50')}>
        <td className="py-2 px-3">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 24}px` }}>
            {hasChildren ? (
              <button
                type="button"
                className="p-0.5 rounded hover:bg-slate-200 text-slate-500"
                onClick={() => onToggle(node.erpId)}
                title={isExpanded ? 'Recolher' : 'Expandir'}
              >
                {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <span className={clsx('text-slate-700', isParent && 'font-semibold')}>{node.description}</span>
            {node.disabled && (
              <span className="ml-2 inline-flex text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Desabilitada</span>
            )}
          </div>
        </td>
        <td className="py-2 px-3 text-center">
          <span className={clsx('inline-flex text-xs rounded-full px-2 py-0.5 font-medium', movementBadgeColor(node.movement))}>
            {movementLabel(node.movement)}
          </span>
        </td>
        <td className="py-2 px-3">
          <div className="flex items-center gap-2">
            <select
              className={selectCls + ' max-w-xs'}
              value={node.inherited || node.dreGroupId == null ? '' : node.dreGroupId}
              disabled={!canWrite || dreSaving}
              onChange={(e) => onDreChange(node, e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">
                {node.parentErpId != null ? '— Herdar do pai —' : '— Sem grupo —'}
              </option>
              {dreGroups.map((group) => (
                <option key={group.dreGroupId} value={group.dreGroupId}>
                  {group.code} — {group.name}
                </option>
              ))}
            </select>
            {dreSaving && <RefreshCw size={14} className="animate-spin text-slate-400" />}
            {node.inherited && node.dreGroupName && (
              <span className="text-xs text-slate-400 whitespace-nowrap">
                {node.dreGroupCode} — {node.dreGroupName} (herdado)
              </span>
            )}
          </div>
        </td>
        {canWrite && (
          <td className="py-2 px-3">
            <div className="flex items-center justify-end gap-1">
              <button
                className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                title="Adicionar subcategoria"
                onClick={() => onAddChild(node)}
              >
                <Plus size={15} />
              </button>
              <button className="p-1.5 text-slate-400 hover:text-emerald-600 rounded" title="Editar" onClick={() => onEdit(node)}>
                <Edit2 size={15} />
              </button>
              <button className="p-1.5 text-slate-400 hover:text-red-500 rounded" title="Excluir" onClick={() => onDelete(node)}>
                <Trash2 size={15} />
              </button>
            </div>
          </td>
        )}
      </tr>
      {hasChildren &&
        isExpanded &&
        node.children.map((child) => (
          <PlanRow
            key={child.erpId}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            canWrite={canWrite}
            dreGroups={dreGroups}
            dreSavingId={dreSavingId}
            onToggle={onToggle}
            onDreChange={onDreChange}
            onAddChild={onAddChild}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
    </>
  );
};

// ─── View Principal ─────────────────────────────────────────────

export const AccountingPlanView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financial.accounting_plan.write').hasPermission;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [filter, setFilter] = useState<FilterChip>('all');

  const [tree, setTree] = useState<AccountingPlanNode[]>([]);
  const [unmapped, setUnmapped] = useState<AccountingPlanNode[]>([]);
  const [dreGroups, setDreGroups] = useState<DreGroup[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dreSavingId, setDreSavingId] = useState<number | null>(null);

  const [planModal, setPlanModal] = useState<PlanModalState | null>(null);

  // Empresas + grupos DRE
  useEffect(() => {
    companyService
      .getCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length > 0) setCompanyId(list[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
    financialService
      .getDreGroups()
      .then((groups) => setDreGroups([...groups].sort((a, b) => a.displayOrder - b.displayOrder)))
      .catch(() => setDreGroups([]));
  }, []);

  const loadTree = useCallback(async () => {
    if (companyId == null) return;
    setLoading(true);
    setError(null);
    try {
      const nodes = await financialService.getAccountingPlans(companyId, false);
      setTree(nodes);
      setExpanded(new Set(nodes.map((n) => n.erpId))); // raízes expandidas por padrão
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar plano de contas');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const loadUnmapped = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nodes = await financialService.getUnmappedAccountingPlans();
      setUnmapped(nodes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar categorias sem classificação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (filter === 'all') loadTree();
    else loadUnmapped();
  }, [filter, loadTree, loadUnmapped]);

  const toggleExpanded = (erpId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(erpId)) next.delete(erpId);
      else next.add(erpId);
      return next;
    });
  };

  // ── Ações ───────────────────────────────────────────────────

  const handleSavePlan = async (data: { description: string; movement: number; disabled: boolean }) => {
    if (companyId == null) throw new Error('Selecione uma empresa');
    if (planModal?.node) {
      await financialService.updateAccountingPlan(planModal.node.accountingPlanId, data);
      setNotice('Categoria atualizada com sucesso');
    } else {
      await financialService.createAccountingPlan({
        companyId,
        description: data.description,
        movement: data.movement,
        ...(planModal?.parent ? { parentErpId: planModal.parent.erpId } : {}),
      });
      setNotice('Categoria criada com sucesso');
    }
    await loadTree();
  };

  const handleDelete = async (node: AccountingPlanNode) => {
    if (!window.confirm(`Excluir a categoria "${node.description}"?`)) return;
    setError(null);
    try {
      await financialService.deleteAccountingPlan(node.accountingPlanId);
      setNotice('Categoria excluída com sucesso');
      await loadTree();
    } catch (err) {
      // 409 da API traz mensagem de conflito (categoria em uso)
      setError(err instanceof Error ? err.message : 'Erro ao excluir categoria');
    }
  };

  const handleDreChange = async (node: AccountingPlanNode, dreGroupId: number | null) => {
    setDreSavingId(node.accountingPlanId);
    setError(null);
    try {
      if (dreGroupId == null) {
        await financialService.clearDreGroup(node.accountingPlanId);
        setNotice(node.parentErpId != null ? 'Grupo DRE removido — herdando do pai' : 'Grupo DRE removido');
      } else {
        await financialService.setDreGroup(node.accountingPlanId, dreGroupId);
        setNotice('Grupo DRE atualizado com sucesso');
      }
      if (filter === 'all') await loadTree();
      else await loadUnmapped();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar grupo DRE');
    } finally {
      setDreSavingId(null);
    }
  };

  const companyName = useMemo(
    () => companies.find((c) => c.company_id === companyId)?.name ?? '',
    [companies, companyId],
  );

  // ── Render ──────────────────────────────────────────────────

  return (
    <PermissionGuard permission="financial.accounting_plan.read" fallback={<PermissionFallback label="o plano de contas" />}>
      <div className="space-y-6">
        {planModal && <PlanModal state={planModal} onSave={handleSavePlan} onClose={() => setPlanModal(null)} />}

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Plano de Contas</h1>
            <p className="text-sm text-slate-500 mt-1">Categorias financeiras e classificação DRE</p>
          </div>
          {canWrite && filter === 'all' && (
            <button className={primaryBtnCls} onClick={() => setPlanModal({ node: null, parent: null })} disabled={companyId == null}>
              <Plus size={16} /> Nova Categoria Raiz
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Empresa</label>
              <select
                className={selectCls + ' w-64'}
                value={companyId ?? ''}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
              >
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-full border transition-colors',
                  filter === 'all'
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50',
                )}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => setFilter('unmapped')}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-full border transition-colors',
                  filter === 'unmapped'
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50',
                )}
              >
                Sem classificação
              </button>
            </div>
          </div>
        </div>

        {notice && <SuccessBox message={notice} onDismiss={() => setNotice(null)} />}
        {error && <ErrorBox message={error} onRetry={filter === 'all' ? loadTree : loadUnmapped} />}
        {loading && <LoadingSpinner />}

        {/* ── Árvore (Todas) ─────────────────────────────────── */}
        {!loading && filter === 'all' && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                    <th className="text-center py-2 px-3 font-semibold text-slate-600 w-28">Movimento</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600 w-96">Grupo DRE</th>
                    {canWrite && <th className="text-right py-2 px-3 font-semibold text-slate-600 w-32">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {tree.length === 0 && (
                    <tr>
                      <td colSpan={canWrite ? 4 : 3} className="py-10 text-center text-slate-400">
                        Nenhuma categoria cadastrada{companyName ? ` para ${companyName}` : ''}
                      </td>
                    </tr>
                  )}
                  {tree.map((node) => (
                    <PlanRow
                      key={node.erpId}
                      node={node}
                      depth={0}
                      expanded={expanded}
                      canWrite={canWrite}
                      dreGroups={dreGroups}
                      dreSavingId={dreSavingId}
                      onToggle={toggleExpanded}
                      onDreChange={handleDreChange}
                      onAddChild={(parent) => setPlanModal({ node: null, parent })}
                      onEdit={(n) => setPlanModal({ node: n, parent: null })}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Lista plana (Sem classificação) ────────────────── */}
        {!loading && filter === 'unmapped' && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                    <th className="text-center py-2 px-3 font-semibold text-slate-600 w-28">Movimento</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600 w-96">Grupo DRE</th>
                  </tr>
                </thead>
                <tbody>
                  {unmapped.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-10 text-center text-slate-400">
                        Todas as categorias estão classificadas 🎉
                      </td>
                    </tr>
                  )}
                  {unmapped.map((node) => (
                    <tr key={node.accountingPlanId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-700">{node.description}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={clsx('inline-flex text-xs rounded-full px-2 py-0.5 font-medium', movementBadgeColor(node.movement))}>
                          {movementLabel(node.movement)}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <select
                            className={selectCls + ' max-w-xs'}
                            value=""
                            disabled={!canWrite || dreSavingId === node.accountingPlanId}
                            onChange={(e) =>
                              e.target.value !== '' && handleDreChange(node, Number(e.target.value))
                            }
                          >
                            <option value="">— Classificar —</option>
                            {dreGroups.map((group) => (
                              <option key={group.dreGroupId} value={group.dreGroupId}>
                                {group.code} — {group.name}
                              </option>
                            ))}
                          </select>
                          {dreSavingId === node.accountingPlanId && (
                            <RefreshCw size={14} className="animate-spin text-slate-400" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default AccountingPlanView;
